import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SuperadminLayout from '../../components/SuperadminLayout';
import LogoCropInput from '../../components/LogoCropInput';
import CompanyGroupsSection from './CompanyGroupsSection';
import {
  createCompany, createCompanyWithLogo, getCompany, updateCompany, updateCompanyWithLogo,
} from '../../api/superadmin';
import { DEFAULT_TICKET_FOOTER } from '../../utils/ticketFooter';
import './SuperadminCompanyFormPage.css';

const emptyForm = {
  name: '', username: '', password: '', address: '', phone: '', is_blocked: false,
  // Imprimer le telephone sur le ticket POS (Company.show_phone_on_ticket).
  show_phone_on_ticket: false,
  offline_sales_enabled: false,
  // Description imprimee en bas des tickets (Company.ticket_footer) : texte par
  // defaut du POS propose a la creation (2026-09-26).
  ticket_footer: DEFAULT_TICKET_FOOTER,
};

const SuperadminCompanyFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [logoFile, setLogoFile] = useState(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    getCompany(id)
      .then((data) => {
        if (cancelled) return;
        setForm({
          name: data.name,
          username: data.username,
          password: '',
          address: data.address || '',
          phone: data.phone || '',
          show_phone_on_ticket: data.show_phone_on_ticket === true,
          is_blocked: data.is_blocked,
          offline_sales_enabled: data.offline_sales_enabled,
          ticket_footer: data.ticket_footer || DEFAULT_TICKET_FOOTER,
        });
        setCurrentLogoUrl(data.logo || null);
      })
      .catch(() => { if (!cancelled) setError('Impossible de charger cette company.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, isEdit]);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      if (logoFile) {
        // Un logo (Blob deja recadre par LogoCropInput) accompagne le formulaire —
        // il faut du multipart, donc un FormData plutot que le payload JSON habituel.
        const fd = new FormData();
        fd.append('name', form.name);
        fd.append('username', form.username);
        if (form.password) fd.append('password', form.password);
        fd.append('address', form.address);
        fd.append('phone', form.phone);
        fd.append('show_phone_on_ticket', form.show_phone_on_ticket ? 'true' : 'false');
        fd.append('ticket_footer', form.ticket_footer);
        fd.append('logo', logoFile, 'logo.png');

        if (isEdit) {
          fd.append('is_blocked', form.is_blocked ? 'true' : 'false');
          fd.append('offline_sales_enabled', form.offline_sales_enabled ? 'true' : 'false');
          const updated = await updateCompanyWithLogo(id, fd);
          setCurrentLogoUrl(updated.logo || null);
          setLogoFile(null);
          setSaved(true);
        } else {
          const created = await createCompanyWithLogo(fd);
          navigate(`/superadmin/companies/${created.id}`);
          return;
        }
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;

        if (isEdit) {
          await updateCompany(id, payload);
          setSaved(true);
        } else {
          const created = await createCompany(payload);
          navigate(`/superadmin/companies/${created.id}`);
          return;
        }
      }
    } catch (err) {
      setError('Sauvegarde impossible — verifie les champs (username unique, password requis a la creation).');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SuperadminLayout>
        <p>Chargement...</p>
      </SuperadminLayout>
    );
  }

  return (
    <SuperadminLayout>
      <Link to="/superadmin/companies" className="scf-back">◀ Retour aux companies</Link>

      <h2 className="scf-title">{isEdit ? 'Modifier la company' : 'Nouvelle company'}</h2>

      <form className="scf-form" onSubmit={handleSubmit}>
        <div className="scf-row">
          <label className="scf-label">Nom</label>
          <input
            className="scf-input"
            type="text"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            required
          />
        </div>

        <div className="scf-row">
          <label className="scf-label">Username</label>
          <input
            className="scf-input"
            type="text"
            value={form.username}
            onChange={(e) => updateField('username', e.target.value)}
            required
          />
        </div>

        <div className="scf-row">
          <label className="scf-label">Password</label>
          <input
            className="scf-input"
            type="password"
            value={form.password}
            onChange={(e) => updateField('password', e.target.value)}
            placeholder={isEdit ? 'Laisser vide pour ne pas changer' : ''}
            required={!isEdit}
          />
        </div>

        <div className="scf-row">
          <label className="scf-label">Adresse</label>
          <input
            className="scf-input"
            type="text"
            value={form.address}
            onChange={(e) => updateField('address', e.target.value)}
          />
        </div>

        <div className="scf-row">
          <label className="scf-label">Telephone</label>
          <input
            className="scf-input"
            type="text"
            value={form.phone}
            onChange={(e) => updateField('phone', e.target.value)}
          />
        </div>

        <label className="scf-checkbox-label">
          <input
            type="checkbox"
            checked={form.show_phone_on_ticket}
            onChange={(e) => updateField('show_phone_on_ticket', e.target.checked)}
          />
          Afficher le numero de telephone sur le ticket
        </label>

        <div className="scf-row scf-row-top">
          <label className="scf-label">Description ticket</label>
          <textarea
            className="scf-input scf-textarea"
            rows={4}
            value={form.ticket_footer}
            onChange={(e) => updateField('ticket_footer', e.target.value)}
            placeholder="Texte imprime en bas du ticket (mention au porteur...)"
          />
        </div>
        <button
          type="button"
          className="scf-link-btn"
          onClick={() => updateField('ticket_footer', DEFAULT_TICKET_FOOTER)}
        >
          Remettre le texte par defaut
        </button>

        <div className="scf-row">
          <label className="scf-label">Logo</label>
          <LogoCropInput currentUrl={currentLogoUrl} onChange={setLogoFile} />
        </div>

        {isEdit && (
          <label className="scf-checkbox-label">
            <input
              type="checkbox"
              checked={form.is_blocked}
              onChange={(e) => updateField('is_blocked', e.target.checked)}
            />
            Bloquee
          </label>
        )}

        {isEdit && (
          <label className="scf-checkbox-label">
            <input
              type="checkbox"
              checked={form.offline_sales_enabled}
              onChange={(e) => updateField('offline_sales_enabled', e.target.checked)}
            />
            Vente hors-ligne autorisee (les banks de cette company peuvent vendre sans connexion)
          </label>
        )}

        {!isEdit && (
          <p className="scf-hint">
            A la creation, un group « group1 » (adresse = celle de la company) et son
            superviseur « superviseur1 » sont crees automatiquement. La company pourra
            definir le mot de passe du superviseur dans Parametres &gt; Superviseurs.
          </p>
        )}

        {error && <p className="scf-error">{error}</p>}
        {saved && <p className="scf-saved">Sauvegarde.</p>}

        <button type="submit" className="scf-save-btn" disabled={saving}>
          {saving ? 'Sauvegarde...' : 'Sauver'}
        </button>
      </form>

      {isEdit && <CompanyGroupsSection companyId={id} />}
    </SuperadminLayout>
  );
};

export default SuperadminCompanyFormPage;
